import { useEffect, useState } from "react";
import "@/App.css";
import {
  Mountain,
  Wifi,
  Tv,
  UtensilsCrossed,
  Thermometer,
  Trees,
  Car,
  Bed,
  Bath,
  Users,
  MapPin,
  Phone,
  Mail,
  ChevronDown,
  Snowflake,
  Coffee,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import ReservationWidget from "@/components/ReservationWidget";
import CalendarWidget from "@/components/CalendarWidget";
import Reviews from "@/components/Reviews";
import VisitCounter from "@/components/VisitCounter";
import Lightbox from "@/components/Lightbox";
import { LangProvider, useLang } from "@/i18n";

const IMG = {
  exterior:
    "https://customer-assets.emergentagent.com/job_jatka-no1/artifacts/0li2q79s_domek.png",
  exteriorOld:
    "https://customer-assets.emergentagent.com/job_2515aa0f-ce67-4775-93db-2197faca5d17/artifacts/ejn62uqb_1.jpg",
  terrace:
    "https://customer-assets.emergentagent.com/job_2515aa0f-ce67-4775-93db-2197faca5d17/artifacts/9rdagyi4_2.jpg",
  livingKitchen:
    "https://customer-assets.emergentagent.com/job_2515aa0f-ce67-4775-93db-2197faca5d17/artifacts/jbueb2rb_3.jpg",
  dining:
    "https://customer-assets.emergentagent.com/job_2515aa0f-ce67-4775-93db-2197faca5d17/artifacts/7g5bp4ht_4.jpg",
  interior:
    "https://customer-assets.emergentagent.com/job_2515aa0f-ce67-4775-93db-2197faca5d17/artifacts/uemej7j9_5.jpg",
  bathroom1:
    "https://customer-assets.emergentagent.com/job_jatka-no1/artifacts/drhfp4o4_11.jpg",
  bathroom2:
    "https://customer-assets.emergentagent.com/job_jatka-no1/artifacts/84lue82p_12.jpg",
  bathroom3:
    "https://customer-assets.emergentagent.com/job_jatka-no1/artifacts/wjzuavz5_13.jpg",
  view:
    "https://customer-assets.emergentagent.com/job_jatka-no1/artifacts/m6l7q5f1_14.jpg",
  bedroom:
    "https://customer-assets.emergentagent.com/job_jatka-no1/artifacts/mj675xyn_15.jpg",
};

const AMENITY_ICONS = [Wifi, UtensilsCrossed, Tv, Trees, Car, Snowflake, Coffee, Thermometer];

const HIGHLIGHTS = [
  { icon: Users, value: "6", key: "guests" },
  { icon: Bed, value: "2", key: "bedrooms" },
  { icon: Bath, value: "2", key: "bathrooms" },
  { icon: Mountain, value: "Tatry", key: "view" },
];

const NEARBY_KEYS = ["gubalowka", "krupowki", "ski", "tpn", "kasprowy", "aquapark"];

function LangBtn({ code, lang, setLang }) {
  return (
    <button
      onClick={() => setLang(code)}
      data-testid={`lang-${code}`}
      className={`px-2 py-1 text-[11px] tracking-widest uppercase transition-colors ${
        lang === code ? "text-[#c89b6a]" : "text-[#e7d8bc]/60 hover:text-[#f3e3c6]"
      }`}
    >
      {code}
    </button>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();
  const links = [
    { id: "o-domku", label: t.nav.about },
    { id: "galeria", label: t.nav.gallery },
    { id: "udogodnienia", label: t.nav.amenities },
    { id: "okolica", label: t.nav.location },
    { id: "rezerwacja", label: t.nav.reservation },
    { id: "opinie", label: t.nav.reviews },
    { id: "kontakt", label: t.nav.contact },
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#1d140d]/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2 text-[#f3e3c6]">
          <Mountain className="w-5 h-5 text-[#c89b6a]" />
          <span className="font-serif tracking-[0.18em] text-sm">JATKA <span className="text-[#c89b6a]">NO1</span></span>
        </a>
        <nav className="hidden md:flex items-center gap-7 text-[13px] text-[#e7d8bc]/85">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-${l.id}`}
              className="hover:text-[#f3e3c6] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#rezerwacja"
            data-testid="nav-cta-reserve"
            className="ml-2 px-4 py-2 rounded-full bg-[#c89b6a] text-[#1d140d] font-medium hover:bg-[#d8ad7e] transition-colors"
          >
            {t.nav.reserve}
          </a>
          <div className="ml-2 flex items-center gap-0 border-l border-white/15 pl-3">
            <LangBtn code="pl" lang={lang} setLang={setLang} />
            <span className="text-white/20">/</span>
            <LangBtn code="en" lang={lang} setLang={setLang} />
          </div>
        </nav>
        <button
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#f3e3c6] p-2"
          aria-label="Menu"
        >
          <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#1d140d]/95">
          <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col gap-3 text-[#e7d8bc]">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="py-1.5 text-sm"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-3 mt-1 border-t border-white/10 flex items-center gap-1" data-testid="mobile-lang-switcher">
              <span className="text-[10px] uppercase tracking-widest text-[#e7d8bc]/50 mr-2">Język</span>
              <LangBtn code="pl" lang={lang} setLang={setLang} />
              <span className="text-white/20">/</span>
              <LangBtn code="en" lang={lang} setLang={setLang} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  const { t } = useLang();
  return (
    <section id="top" className="relative min-h-[100svh] flex items-end overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center scale-105" style={{ backgroundImage: `url(${IMG.exterior})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1d140d]/70 via-[#1d140d]/30 to-[#1d140d]/95" />
      <div className="absolute top-24 right-6 md:right-10 hidden sm:flex items-center gap-2 text-[#f3e3c6]/80 text-xs tracking-[0.25em] uppercase">
        <span className="h-px w-8 bg-[#c89b6a]" /> Zakopane · Suche
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-32 text-[#f3e3c6]">
        <p className="text-xs tracking-[0.35em] uppercase text-[#c89b6a] mb-5" data-testid="hero-eyebrow">{t.hero.eyebrow}</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl">
          JATKA <span className="italic text-[#c89b6a]">NO1</span>
          <br />
          <span className="text-[#f3e3c6]/90">{t.hero.titleSub}</span>
        </h1>
        <p className="mt-6 max-w-xl text-[#e7d8bc]/85 text-base sm:text-lg leading-relaxed">{t.hero.desc}</p>
        <div className="mt-9 flex flex-wrap items-center gap-3">
          <a href="#rezerwacja" data-testid="hero-cta-reserve" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#c89b6a] text-[#1d140d] font-medium hover:bg-[#d8ad7e] transition-colors">{t.hero.cta1}</a>
          <a href="#galeria" data-testid="hero-cta-gallery" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#f3e3c6]/40 text-[#f3e3c6] hover:bg-[#f3e3c6]/10 transition-colors">{t.hero.cta2}</a>
          <a
            href="https://www.warta.pl/kalkulator/ubezpieczenie-turystyczne/twoja-podroz?p=MS10OFF"
            target="_blank"
            rel="noopener sponsored"
            data-testid="hero-cta-insurance"
            className="group relative inline-flex items-center gap-2 px-5 py-3 rounded-full border border-dashed border-[#c89b6a]/60 text-[#e7d8bc]/85 text-sm hover:text-[#1d140d] hover:bg-[#c89b6a] hover:border-[#c89b6a] transition-all"
          >
            <ShieldCheck className="w-4 h-4 text-[#c89b6a] group-hover:text-[#1d140d] transition-colors" />
            <span>{t.hero.cta3}</span>
            <span
              role="tooltip"
              data-testid="hero-cta-insurance-tooltip"
              className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-11 whitespace-nowrap px-3 py-1.5 rounded-full bg-[#c89b6a] text-[#1d140d] text-[11px] font-medium tracking-wide opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 shadow-lg"
            >
              {t.hero.cta3Tip}
            </span>
          </a>
        </div>
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
          {HIGHLIGHTS.map((h) => (
            <div key={h.key} data-testid={`hero-stat-${h.key}`} className="flex items-center gap-3 border-l border-[#c89b6a]/60 pl-3">
              <h.icon className="w-5 h-5 text-[#c89b6a]" />
              <div>
                <div className="font-serif text-lg leading-none">{h.value}</div>
                <div className="text-[11px] uppercase tracking-widest text-[#e7d8bc]/70">{t.hero.stats[h.key]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  const { t } = useLang();
  return (
    <section id="o-domku" className="py-24 lg:py-32 bg-[#faf6ee]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.35em] uppercase text-[#8a6135]" data-testid="about-eyebrow">
            {t.about.eyebrow}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#2a1d12] mt-4 leading-tight">
            {t.about.title[0]}<span className="italic">{t.about.title[1]}</span>{t.about.title[2]}
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-5 text-[#3b2a1c] text-[15px] leading-[1.85]">
          <p>{t.about.p1}</p>
          <p>{t.about.p2}</p>
          <div className="pt-3 flex flex-wrap gap-3 text-[12px] tracking-wide">
            {t.about.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full bg-[#2a1d12]/5 text-[#3b2a1c] border border-[#2a1d12]/10"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  const { t } = useLang();
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const items = [
    { src: IMG.exterior, alt: "Domek JATKA NO1 z zewnątrz", testid: "gallery-img-exterior", cls: "col-span-12 md:col-span-8 row-span-2", h: "h-72 md:h-[520px]" },
    { src: IMG.terrace, alt: "Taras z widokiem", testid: "gallery-img-terrace", cls: "col-span-6 md:col-span-4", h: "h-44 md:h-[250px]" },
    { src: IMG.livingKitchen, alt: "Salon z kuchnią", testid: "gallery-img-living", cls: "col-span-6 md:col-span-4", h: "h-44 md:h-[254px]" },
    { src: IMG.dining, alt: "Jadalnia i kamienna ściana", testid: "gallery-img-dining", cls: "col-span-6 md:col-span-6", h: "h-48 md:h-[320px]" },
    { src: IMG.interior, alt: "Salon i schody na poddasze", testid: "gallery-img-interior", cls: "col-span-6 md:col-span-6", h: "h-48 md:h-[320px]" },
    { src: IMG.bedroom, alt: "Sypialnia na poddaszu", testid: "gallery-img-bedroom", cls: "col-span-12 md:col-span-4", h: "h-56 md:h-[360px]" },
    { src: IMG.view, alt: "Widok z tarasu na góry", testid: "gallery-img-view", cls: "col-span-12 md:col-span-8", h: "h-56 md:h-[360px]" },
    { src: IMG.bathroom1, alt: "Łazienka — drewno i marmur", testid: "gallery-img-bathroom1", cls: "col-span-6 md:col-span-4", h: "h-44 md:h-[260px]" },
    { src: IMG.bathroom2, alt: "Druga łazienka z bidetem", testid: "gallery-img-bathroom2", cls: "col-span-6 md:col-span-4", h: "h-44 md:h-[260px]" },
    { src: IMG.bathroom3, alt: "Łazienka z pralką", testid: "gallery-img-bathroom3", cls: "col-span-12 md:col-span-4", h: "h-44 md:h-[260px]" },
  ];

  const close = () => setLightboxIdx(null);
  const prev = () => setLightboxIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length));
  const next = () => setLightboxIdx((i) => (i === null ? null : (i + 1) % items.length));

  return (
    <section id="galeria" className="py-24 lg:py-32 bg-[#1d140d] text-[#f3e3c6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase text-[#c89b6a]" data-testid="gallery-eyebrow">
              {t.gallery.eyebrow}
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-3">
              {t.gallery.title[0]}<span className="italic text-[#c89b6a]">{t.gallery.title[1]}</span>
            </h2>
          </div>
          <p className="max-w-md text-sm text-[#e7d8bc]/75">{t.gallery.sub}</p>
        </div>

        <div className="grid grid-cols-12 gap-3 sm:gap-4">
          {items.map((it, idx) => (
            <figure
              key={it.testid}
              onClick={() => setLightboxIdx(idx)}
              className={`${it.cls} overflow-hidden rounded-2xl group cursor-zoom-in relative`}
            >
              <img
                src={it.src}
                alt={it.alt}
                data-testid={it.testid}
                className={`w-full ${it.h} object-cover transition-transform duration-700 group-hover:scale-110`}
              />
              <span className="absolute inset-0 bg-[#1d140d]/0 group-hover:bg-[#1d140d]/15 transition-colors" />
            </figure>
          ))}
        </div>
      </div>

      <Lightbox images={items} index={lightboxIdx} onClose={close} onPrev={prev} onNext={next} />
    </section>
  );
}

function Amenities() {
  const { t } = useLang();
  return (
    <section id="udogodnienia" className="py-24 lg:py-32 bg-[#faf6ee]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.35em] uppercase text-[#8a6135]" data-testid="amenities-eyebrow">
            {t.amenities.eyebrow}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#2a1d12] mt-3 leading-tight">
            {t.amenities.title[0]}<span className="italic">{t.amenities.title[1]}</span>{t.amenities.title[2]}
          </h2>
          <p className="mt-5 text-[#3b2a1c]/80 text-[15px] leading-relaxed">{t.amenities.sub}</p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.amenities.items.map((a, idx) => {
            const Icon = AMENITY_ICONS[idx];
            return (
              <div
                key={a.l}
                data-testid={`amenity-${idx}`}
                className="group p-6 rounded-2xl bg-white border border-[#2a1d12]/8 hover:border-[#c89b6a]/60 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-[#2a1d12]/5 group-hover:bg-[#c89b6a]/15 flex items-center justify-center mb-5 transition-colors">
                  <Icon className="w-5 h-5 text-[#8a6135]" />
                </div>
                <h3 className="font-serif text-lg text-[#2a1d12]">{a.l}</h3>
                <p className="text-sm text-[#3b2a1c]/70 mt-1.5 leading-relaxed">{a.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Location() {
  const { t } = useLang();
  return (
    <section id="okolica" className="py-24 lg:py-32 bg-[#efe6d3] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.35em] uppercase text-[#8a6135]" data-testid="location-eyebrow">
              {t.location.eyebrow}
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2a1d12] mt-3 leading-tight">
              {t.location.title[0]}<span className="italic">{t.location.title[1]}</span>{t.location.title[2]}
            </h2>
            <div className="mt-7 p-5 rounded-2xl bg-white/70 border border-[#2a1d12]/10">
              <div className="flex items-start gap-3 text-[#2a1d12]">
                <MapPin className="w-5 h-5 mt-0.5 text-[#8a6135]" />
                <div>
                  <div className="font-medium">{t.location.address1}</div>
                  <div className="text-sm text-[#3b2a1c]/80">{t.location.address2}</div>
                </div>
              </div>
              <div className="mt-4 rounded-xl overflow-hidden border border-[#2a1d12]/10" data-testid="location-map-embed">
                <iframe
                  title="Mapa — Suche 151B, 34-500 Zakopane"
                  src="https://www.google.com/maps?q=Suche%20151B%2C%2034-500%20Zakopane&hl=pl&z=14&output=embed"
                  width="100%"
                  height="220"
                  style={{ border: 0, display: "block" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="divide-y divide-[#2a1d12]/10 border-y border-[#2a1d12]/10">
              {t.location.nearby.map((n, idx) => (
                <li
                  key={NEARBY_KEYS[idx]}
                  data-testid={`nearby-${NEARBY_KEYS[idx]}`}
                  className="py-5 flex items-center justify-between gap-6 group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-xl text-[#2a1d12] group-hover:text-[#8a6135] transition-colors">
                      {n.n}
                    </div>
                    <div className="text-sm text-[#3b2a1c]/75 mt-1">{n.note}</div>
                  </div>
                  <div className="text-sm tracking-wide text-[#8a6135] whitespace-nowrap">{n.d}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Reservation() {
  const { t } = useLang();
  return (
    <section id="rezerwacja" className="py-24 lg:py-32 bg-[#faf6ee]">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.35em] uppercase text-[#8a6135]" data-testid="reservation-eyebrow">
            {t.reservation.eyebrow}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#2a1d12] mt-3 leading-tight">
            {t.reservation.title[0]}<span className="italic">{t.reservation.title[1]}</span>
          </h2>
          <p className="mt-5 text-[#3b2a1c]/80 max-w-xl mx-auto text-[15px]">{t.reservation.sub}</p>
        </div>

        <div
          data-testid="availability-calendar-container"
          className="rounded-2xl bg-white border border-[#2a1d12]/10 p-2 sm:p-4 shadow-sm mb-6"
        >
          <div className="px-3 pt-3 pb-1 flex flex-wrap items-center gap-4 text-[12px] text-[#3b2a1c]/75">
            <div className="flex items-center gap-2" data-testid="legend-available">
              <span className="w-3 h-3 rounded-sm bg-[#2E7D32]" />
              <span>{t.reservation.legend.free}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="legend-occupied">
              <span className="w-3 h-3 rounded-sm bg-[#B23A3A]" />
              <span>{t.reservation.legend.busy}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="legend-past">
              <span className="w-3 h-3 rounded-sm bg-[#D3D3D3]" />
              <span>{t.reservation.legend.past}</span>
            </div>
          </div>
          <CalendarWidget />
        </div>

        <div
          data-testid="reservation-widget-container"
          className="rounded-2xl bg-white border border-[#2a1d12]/10 p-2 sm:p-4 shadow-sm"
        >
          <ReservationWidget />
        </div>
        <p className="mt-6 text-center text-xs text-[#3b2a1c]/60">{t.reservation.footer}</p>
      </div>
    </section>
  );
}

function Contact() {
  const { t } = useLang();
  return (
    <section id="kontakt" className="py-24 lg:py-32 bg-[#1d140d] text-[#f3e3c6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.35em] uppercase text-[#c89b6a]" data-testid="contact-eyebrow">
            {t.contact.eyebrow}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">
            {t.contact.title[0]}<span className="italic text-[#c89b6a]">{t.contact.title[1]}</span>{t.contact.title[2]}
          </h2>
          <p className="mt-5 text-[#e7d8bc]/80 text-[15px] leading-relaxed">{t.contact.sub}</p>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          <a
            href="tel:+48698761036"
            data-testid="contact-phone"
            className="p-6 rounded-2xl border border-white/10 hover:border-[#c89b6a]/60 hover:bg-white/5 transition-all group"
          >
            <Phone className="w-5 h-5 text-[#c89b6a] mb-4" />
            <div className="text-[11px] uppercase tracking-widest text-[#e7d8bc]/60">{t.contact.phone}</div>
            <div className="font-serif text-2xl mt-1 group-hover:text-[#c89b6a] transition-colors">
              698 761 036
            </div>
          </a>
          <a
            href="mailto:Jatkano1@gmail.com"
            data-testid="contact-email"
            className="p-6 rounded-2xl border border-white/10 hover:border-[#c89b6a]/60 hover:bg-white/5 transition-all group"
          >
            <Mail className="w-5 h-5 text-[#c89b6a] mb-4" />
            <div className="text-[11px] uppercase tracking-widest text-[#e7d8bc]/60">{t.contact.email}</div>
            <div className="font-serif text-2xl mt-1 group-hover:text-[#c89b6a] transition-colors break-all">
              Jatkano1@gmail.com
            </div>
          </a>
          <div className="p-6 rounded-2xl border border-white/10 sm:col-span-2" data-testid="contact-address">
            <MapPin className="w-5 h-5 text-[#c89b6a] mb-4" />
            <div className="text-[11px] uppercase tracking-widest text-[#e7d8bc]/60">{t.contact.address}</div>
            <div className="font-serif text-xl mt-1">Suche 151B, 34-500 Zakopane</div>
            <div className="text-sm text-[#e7d8bc]/70 mt-1">{t.contact.host}</div>
          </div>
        </div>
      </div>

      <footer className="mt-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#e7d8bc]/60">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#c89b6a]" />
            <span>© {new Date().getFullYear()} JATKA NO1 · Zakopane</span>
          </div>
          <VisitCounter />
          <div>{t.contact.rights}</div>
        </div>
      </footer>
    </section>
  );
}

export default function App() {
  useEffect(() => {
    document.title = "JATKA NO1 · Domek w Zakopanem (Suche) — wynajem";
  }, []);

  return (
    <LangProvider>
      <div className="App font-sans text-[#2a1d12] bg-[#faf6ee]" data-testid="app-root">
        <Header />
        <Hero />
        <About />
        <Gallery />
        <Amenities />
        <Location />
        <Reservation />
        <Reviews />
        <Contact />
      </div>
    </LangProvider>
  );
}
